import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  closestCenter,
  defaultDropAnimation,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  MeasuringStrategy,
  Modifiers,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { FlattenedItem } from '@/shared/builder'
import { LeadingSidebarTab } from '@/shared/builder-ui'

import { useSortableTreeContext } from '../contexts/SortableTreeContext'
import { filterParentDeep, getChildCount, getProjection } from '../utils'
import { SortableTreeItem } from './SortableTreeItem'

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
        }),
      },
    ]
  },
  easing: 'ease-out',
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    })
  },
}

const modifiersConfig: Modifiers = [
  ({ transform }) => {
    return { ...transform }
  },
]

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
}

function isValidDrop(
  activeComp: FlattenedItem | undefined,
  projected?: any,
  itemsMap: Map<string, FlattenedItem> = new Map()
) {
  if (!activeComp || !projected) return true

  // prevent form component from being moved outside of a form container, could be nested
  if (activeComp.isForm || activeComp.isSubmit) {
    if (!projected.parentId) {
      return false
    }
    // check if it has form parent
    const isFormParent = filterParentDeep(
      itemsMap,
      projected.parentId,
      (parent) => parent.compId === 'form' || parent.parentType === 'form'
    )

    return isFormParent
  }

  if (
    // prevent tab-item from being moved to non-tabs
    (activeComp.compId === 'tab-item' && projected.parentType !== 'tabs') ||
    // prevent non-tab-item from being moved to tabs
    (activeComp.compId !== 'tab-item' && projected.parentType === 'tabs')
  ) {
    return false
  }

  return true
}

/**
 * SortableTree
 */
export function SortableTree({ indentationWidth = 24 }: { indentationWidth?: number }) {
  const { activeLayer, activeComponent, setActiveComponent } = useActiveLayerContext()
  const { activeTab, setActiveTab, setShowFabWarningModal, setPlaceholderParentId, setFabWarningReason } =
    useAppBuilderContext()

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (activeComponent?.id && containerRef.current) {
      const activeElement = containerRef.current.querySelector(`[data-id="${activeComponent.id}"]`)
      if (activeElement) {
        const container = containerRef.current
        const headerHeight = 48

        const elementRect = activeElement.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()

        const targetScrollTop = container.scrollTop + (elementRect.top - containerRect.top) - headerHeight

        container.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth',
        })
      }
    }
  }, [activeComponent?.id])

  const {
    activeId,
    setActiveId,
    flattenedItems,
    flattenedItemsMap,
    sortableTreeItems,
    handleReorder,
    elements,
    handleCollapse,
    moveToGlobals,
    moveFromGlobals,
    isInGlobals,
  } = useSortableTreeContext()

  const sortedIds = useMemo(() => sortableTreeItems.map(({ id }) => id), [sortableTreeItems])
  const filteredItems = sortableTreeItems

  const activeItem = activeId ? flattenedItemsMap.get(activeId) : undefined

  //--------------------------------------------------------------------------------------//
  //                                  DnD Context                                         //
  //--------------------------------------------------------------------------------------//

  // Initialize sensors for pointer (mouse or touch) and keyboard interactions.
  const [overId, setOverId] = useState<string | null>(null)
  const [offsetLeft, setOffsetLeft] = useState(0)
  const [isValidAction, setIsValidAction] = useState(true)

  const projected =
    activeId && overId ? getProjection(sortableTreeItems, activeId, overId, offsetLeft, indentationWidth) : null

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 0,
        tolerance: 4, // require 4px to activate drag movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  function resetState() {
    setOverId(null)
    setActiveId(null)
    setOffsetLeft(0)
  }

  function handleDragStart({ active: { id: activeId } }: DragStartEvent) {
    setActiveId(activeId as string)
    setOverId(activeId as string)
  }

  function handleDragMove({ active, over, delta }: DragMoveEvent) {
    setOffsetLeft(delta.x)

    const activeId = active.id
    const activeComp = flattenedItemsMap.get(activeId.toString())

    if (!!activeComp || !!projected) {
      const isValid = isValidDrop(activeComp, projected, flattenedItemsMap)
      let isValidIndex = true

      // prevent dragging items before Header at root
      if (over) {
        const overIndex = flattenedItems.findIndex(({ id }) => id === over.id)
        const headerIdx = flattenedItems.findIndex(
          (n) => n.groupId === 'globals' && n.compId === 'header' && n.parentId == null
        )
        if (headerIdx !== -1 && headerIdx === overIndex) {
          isValidIndex = false
        }
      }

      setIsValidAction(isValid && isValidIndex)
    }
  }

  function handleDragOver({ over }: DragOverEvent) {
    const overId = over?.id ? over?.id.toString() : null

    if (overId) {
      setOverId(overId)
    }
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    resetState()

    if (!isValidAction) {
      setIsValidAction(true)
      return
    }

    if (projected && over) {
      const { depth, parentId, parentTitle } = projected

      if (depth > 0 && !parentId) {
        return
      }

      let clonedItems: FlattenedItem[] = [...flattenedItems]

      const overIndex = clonedItems.findIndex(({ id }) => id === over.id)
      const activeIndex = clonedItems.findIndex(({ id }) => id === active.id)
      const activeItem = clonedItems[activeIndex]
      // const overItem = clonedItems[overIndex]
      // const parent = clonedItems.find(({ id }) => id === parentId)

      // Handle dropping onto placeholder
      if (over.id.toString().startsWith('placeholder-')) {
        const placeholderParentId = over.id.toString().replace('placeholder-', '')
        const parentItem = clonedItems.find(({ id }) => id === placeholderParentId)

        // prevent moving tab items to another tab item
        if (activeItem.compId === 'tab-item' && parentItem?.compId === 'tab-item') {
          return
        }

        const activeIsInGlobals = isInGlobals(activeItem.id)
        const targetIsInGlobals = parentItem ? isInGlobals(placeholderParentId) : false

        // If moving from page to globals, use moveToGlobals
        if (parentItem && targetIsInGlobals && !activeIsInGlobals) {
          console.log('move to globals placeholder')
          moveToGlobals(activeItem.id, placeholderParentId)
          return
        }

        // If moving from globals to page, use moveFromGlobals
        if (parentItem && !targetIsInGlobals && activeIsInGlobals) {
          console.log('move from globals placeholder')
          moveFromGlobals(activeItem.id, placeholderParentId)
          return
        }

        // Otherwise, handle as a regular reorder (within page or within globals)
        if (parentItem) {
          // Update the item with new parent relationship
          clonedItems[activeIndex] = {
            ...activeItem,
            depth: parentItem.depth + 1,
            parentId: placeholderParentId,
            parentTitle: parentItem.title,
          }

          // Calculate the correct insertion index: right after the parent
          const parentIndex = clonedItems.findIndex(({ id }) => id === placeholderParentId)
          const targetIndex = parentIndex + 1

          const newFlattenedItems = arrayMove(clonedItems, activeIndex, targetIndex)
          handleReorder(newFlattenedItems)
          return
        }
      }

      if (activeItem.compId === 'fab' && parentId) {
        // prevent inserting FAB inside Header (including nested under Header)
        const isInHeader = filterParentDeep(
          flattenedItemsMap,
          parentId,
          (parent) => parent.groupId === 'globals' && parent.compId === 'header'
        )
        if (isInHeader) {
          setFabWarningReason('header')
          setShowFabWarningModal(true)
          return
        }

        // prevent multiple FABs inside the same Global Footer subtree
        const findFooterAncestorId = (startId: string | null | undefined): string | null => {
          let curId = startId || null
          while (curId) {
            const node = flattenedItemsMap.get(curId)
            if (node && node.groupId === 'globals' && node.compId === 'footer') {
              return node.id
            }
            curId = node?.parentId || null
          }
          return null
        }
        const footerAncestorId = findFooterAncestorId(parentId)
        if (footerAncestorId) {
          const hasFabInFooter = flattenedItems.some(
            (it) =>
              it.compId === 'fab' &&
              it.id !== activeItem.id &&
              filterParentDeep(flattenedItemsMap, it.id, (p) => p.id === footerAncestorId)
          )
          if (hasFabInFooter) {
            setFabWarningReason('limit')
            setShowFabWarningModal(true)
            return
          }
        }
      }

      // Handle moving from globals to page (non-placeholder drop)
      const activeIsInGlobals = isInGlobals(activeItem.id)
      const targetIsInGlobals = parentId ? isInGlobals(parentId) : false

      if (activeIsInGlobals && !targetIsInGlobals) {
        console.log('move from globals to page (non-placeholder)')
        moveFromGlobals(activeItem.id, parentId || undefined)
        return
      }

      // Handle moving to globals (non-placeholder drop)
      if (parentId && !activeIsInGlobals && targetIsInGlobals) {
        console.log('move to globals (non-placeholder)')
        moveToGlobals(activeItem.id, parentId)
        return
      }

      clonedItems[activeIndex] = {
        ...activeItem,
        depth,
        parentId,
        parentTitle,
      }

      const newFlattenedItems = arrayMove(clonedItems, activeIndex, overIndex)

      // Guard: prevent placing non-global items before Header or after Footer at root
      // Only enforce this if we're moving a page element (not moving within globals)
      const movedItemIsInGlobals = isInGlobals(activeItem.id)

      if (!movedItemIsInGlobals && Array.isArray(newFlattenedItems) && newFlattenedItems.length) {
        const headerIdx = newFlattenedItems.findIndex(
          (n) => n.groupId === 'globals' && n.compId === 'header' && n.parentId == null
        )
        const footerIdx = newFlattenedItems.findIndex(
          (n) => n.groupId === 'globals' && n.compId === 'footer' && n.parentId == null
        )

        // Check if the moved item (page element) is before Header or after Footer
        const movedItemNewIndex = newFlattenedItems.findIndex(({ id }) => id === activeItem.id)

        if (headerIdx !== -1 && movedItemNewIndex < headerIdx) {
          console.log('prevent placing non-global items before Header at root')
          return
        }
        if (footerIdx !== -1 && movedItemNewIndex > footerIdx) {
          console.log('prevent placing non-global items after Footer at root')
          return
        }
      }

      handleReorder(newFlattenedItems)
    }
  }

  function handleDragCancel() {
    resetState()
  }

  const shouldShowPlaceholder = (item: FlattenedItem) => {
    if (item.id === activeId) return false
    return ['form', 'section', 'tab-item', 'header', 'footer'].includes(item.compId) && item.children.length === 0
  }

  function handlePlaceholderClick(id: string) {
    setPlaceholderParentId(id)
    if (activeTab !== LeadingSidebarTab.Component) {
      setActiveTab(LeadingSidebarTab.Component)
    }
    setActiveComponent(undefined)
  }

  function handleClick(item: FlattenedItem) {
    if (item.compId === 'tab-item' && item.parentId) {
      let parentItem = flattenedItemsMap.get(item.parentId)
      if (parentItem) {
        setActiveTab(LeadingSidebarTab.Component)
        setActiveComponent({
          layerType: activeLayer.type,
          compId: parentItem.compId,
          id: parentItem.id,
          groupId: parentItem.groupId,
        })
        return
      }
    } else {
      if (activeTab !== LeadingSidebarTab.Component) {
        setActiveTab(LeadingSidebarTab.Component)
      }
      setActiveComponent({
        layerType: activeLayer.type,
        compId: item.compId,
        id: item.id,
        groupId: item.groupId,
      })
    }
  }

  return (
    <div className='overflow-y-auto h-[calc(100%-40px)]' ref={containerRef}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={measuring}
        modifiers={[restrictToFirstScrollableAncestor]}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
          {filteredItems.map((item) => {
            const {
              id,
              parentId,
              icon,
              nested,
              title,
              customTitle,
              children,
              collapsed,
              depth,
              compId,
              removable,
              draggable,
              isSubmit,
              formErrorType,
            } = item
            //

            const isProjected = !!projected?.parentId || projected?.depth === 0
            const isNestable = isProjected && isValidAction
            const isParentForm = compId === 'form'

            return (
              <SortableTreeItem
                key={id}
                id={id}
                parentId={parentId}
                value={id}
                compId={compId}
                groupId={item.groupId}
                activeItem={activeItem}
                nested={nested}
                active={activeComponent?.id === id}
                nestableIndicator={isNestable}
                title={title}
                customTitle={customTitle}
                icon={icon}
                depth={id === activeId && projected ? projected.depth : depth}
                indentationWidth={indentationWidth}
                indicator={true}
                collapsed={Boolean(collapsed && children.length)}
                onCollapse={children?.length ? () => handleCollapse(id) : undefined}
                onClick={() => handleClick(item)}
                removable={removable}
                draggable={draggable}
                formErrorType={formErrorType}
                isParentForm={isParentForm}
                onPlaceholderClick={() => handlePlaceholderClick(id)}
                isSubmit={isSubmit}
                showPlaceholder={shouldShowPlaceholder(item)}
              />
            )
          })}

          {createPortal(
            <DragOverlay dropAnimation={dropAnimationConfig} modifiers={modifiersConfig}>
              {activeId && activeItem && (
                <SortableTreeItem
                  clone
                  id={activeId}
                  value={activeId}
                  depth={activeItem.depth}
                  compId={activeItem.compId}
                  nested={activeItem.nested}
                  title={activeItem.title}
                  icon={activeItem.icon}
                  childCount={getChildCount(elements, activeId) + 1}
                  indentationWidth={indentationWidth}
                  className='px-0 mx-0 bg-zinc-200 dark:bg-slate-900 dark:text-white text-m-m list-none opacity-70 rounded-md'
                />
              )}
            </DragOverlay>,
            document.body
          )}
        </SortableContext>
      </DndContext>
    </div>
  )
}
